import Box from "@mui/material/Box"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { type ChangeEvent, type FC, useState } from "react"

import { useCostBreakdownTable } from "../hooks/useCostBreakdownTable"

import { CostBreakdownDialogs } from "./CostBreakdownDialogs"
import { CostBreakdownFilterDrawer, CostBreakdownTable } from "./CostBreakdownTable"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { GoodsReceiptFilterChips } from "@/screens/WarehouseReceiving/components"

export const CostBreakdownContainer: FC = () => {
  const navigate = useNavigate({ from: "/app/procurement/cost-breakdown" })
  const searchParams = useSearch({
    from: "/app/procurement/_layout/cost-breakdown",
    strict: true,
  })

  const {
    searchQuery,
    warehouseAreaIds,
    warehouseOptions,
    isWarehousesFetching,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleRefetch,
  } = useCostBreakdownTable()

  const isProductCostCompleted = searchParams.isProductCostCompleted ?? []

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [isDialogsOpen, setIsDialogsOpen] = useState(false)
  const [selectedGoodsReceipt, setSelectedGoodsReceipt] = useState<GoodsReceipt | undefined>(undefined)
  const [selectedPurchaseOrderLineId, setSelectedPurchaseOrderLineId] = useState<string | undefined>(undefined)

  const handleCostEdit = (goodsReceipt: GoodsReceipt) => {
    setSelectedGoodsReceipt(goodsReceipt)
    setIsDialogsOpen(true)
  }

  const handleProductCostEdit = (goodsReceipt: GoodsReceipt, purchaseOrderLineId: string) => {
    setSelectedGoodsReceipt(goodsReceipt)
    setSelectedPurchaseOrderLineId(purchaseOrderLineId)
    setIsDialogsOpen(true)
  }

  const handleCloseDialogs = () => {
    setIsDialogsOpen(false)
    setSelectedGoodsReceipt(undefined)
    setSelectedPurchaseOrderLineId(undefined)
  }

  const handleSuccess = () => {
    handleRefetch()
    handleCloseDialogs()
  }

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(event.target.value)
  }

  const handleFilterDrawerToggle = () => {
    setIsFilterDrawerOpen((prev) => !prev)
  }

  const handleCostBreakdownFilterChange = (isProductCostCompleted?: boolean[]) => {
    ;(navigate as any)({
      search: (prev: Record<string, any>) => ({
        ...prev,
        isProductCostCompleted,
      }),
      replace: true,
    })
  }

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      <Box className='flex items-center gap-x-4'>
        <SearchInput
          placeholder='Search by GR, Supplier, Tracking, Container or PO'
          defaultValue={searchQuery}
          onChange={handleSearchInputChange}
          className='w-full max-w-[524px]'
        />
        <Button appearance='outlined' startIcon={<FilterAltOutlined />} onClick={handleFilterDrawerToggle}>
          Filter
        </Button>
      </Box>

      {warehouseAreaIds.length > 0 && (
        <GoodsReceiptFilterChips
          warehouseAreaIds={warehouseAreaIds}
          warehouseOptions={warehouseOptions}
          onRemove={handleFilterRemove}
        />
      )}

      <CostBreakdownTable onCostEdit={handleCostEdit} onProductCostEdit={handleProductCostEdit} />

      <CostBreakdownFilterDrawer
        isOpen={isFilterDrawerOpen}
        warehouseAreaIds={warehouseAreaIds}
        warehouseOptions={warehouseOptions}
        isWarehousesFetching={isWarehousesFetching}
        isProductCostCompleted={isProductCostCompleted}
        onToggle={handleFilterDrawerToggle}
        onChange={handleFilterChange}
        onCostBreakdownChange={handleCostBreakdownFilterChange}
        onClear={handleFilterClear}
      />

      {isDialogsOpen && (
        <CostBreakdownDialogs
          goodsReceipt={selectedGoodsReceipt}
          onClose={handleCloseDialogs}
          onSuccess={handleSuccess}
          purchaseOrderLineId={selectedPurchaseOrderLineId}
        />
      )}
    </Box>
  )
}
